import classNames from 'classnames';
import sum from 'lodash/sum';
import * as React from 'react';
import { usePlannerContext } from '../Planner/Context/PlannerContextProvider';
import { usePlannerTranslations } from '../Planner/Context/PlannerTranslationsContextProvider';
import type { SubjectListProps } from '../SubjectList/SubjectList';
import { SubjectList } from '../SubjectList/SubjectList';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { FormattedText } from '~/Features/Txt/TxtBase';
import { AccordionItem } from '~/Partials/AccordionComponent/AccordionItem';
import type { Theme } from '~/Utils/Theme';
import styles from './SubjectListAccordion.module.css';

export interface SubjectListAccordionProps extends SubjectListProps {
    heading: string;
    semester: string;
    recommendedPointLimit: number;
    theme?: Pick<Theme, 'withRoundedCorners'>;
    className?: string;
}

export const SubjectListAccordion: React.FC<SubjectListAccordionProps> = props => {
    const contextualTheme = useTheme();
    const {
        heading,
        semester,
        recommendedPointLimit,
        theme = contextualTheme,
        className,
        subjects,
        ...restProps
    } = props;
    const { withRoundedCorners } = theme;

    const { selectedElectiveSubjects } = usePlannerContext();
    const { description, creditsLabel } = usePlannerTranslations();
    const { selectedSubjectsFormat, fromRecommendedCreditsFormat } = description;

    const subjectsCodes = subjects.map(subject => subject.subjectCode);

    const selectedElectiveSubjectsForCurrentSemester = selectedElectiveSubjects.filter(
        subject => subjectsCodes.includes(subject.subjectCode) && subject.semester === semester
    );
    const numberOfElementsSelected = selectedElectiveSubjectsForCurrentSemester.length;
    const selectedPoints = sum(selectedElectiveSubjectsForCurrentSemester.map(subject => subject.points.amount));

    const renderHeader = () => (
        <div className={styles.Header}>
            <div className={styles.HeaderSubjectSelection}>
                <span className={styles.HeaderTitle}>{heading}</span>
                <span
                    className={classNames(
                        styles.HeaderSelectedElements,
                        numberOfElementsSelected <= 0 && styles['HeaderSelectedElements--hidden']
                    )}
                >
                    {FormattedText(selectedSubjectsFormat, { subjects: numberOfElementsSelected })}
                </span>
            </div>
            <div>
                <span className={styles.HeaderSelectedPointsValue}>{selectedPoints}</span>
                <span className={styles.HeaderSelectedPointsDescription}>
                    {FormattedText(fromRecommendedCreditsFormat, {
                        credits: recommendedPointLimit,
                        creditsName: creditsLabel,
                    })}
                </span>
            </div>
        </div>
    );

    return (
        <div className={classNames(withRoundedCorners && styles['Variant--rounded'], className)}>
            <AccordionItem
                className={styles.Accordion}
                expanderClassName={styles.AccordionExpander}
                headerContent={renderHeader()}
                iconType="chevron"
                variant="clear"
                lazyRender
            >
                <SubjectList {...restProps} subjects={subjects} className={styles.AccordionContent} />
            </AccordionItem>
        </div>
    );
};
